import { useCallback, useEffect, useState } from 'react'

import type { Resource } from '../../api/client.ts'
import {
  fetchBookings,
  type Booking,
  type BookingStatusFilter,
} from '../../api/bookings.ts'
import { useApiFetch } from '../../ghost/GhostProvider.tsx'

// The bookings feed: one keyset-paged read, same shape as the webhooks
// dashboard's attempts feed (routes/webhooks/useWebhookData.ts) — loading /
// ready / failed, a load-more that *appends*, and a load-more failure that
// never discards what the reader already has.
//
// Reads through `useApiFetch` (src/ghost) so a Curri admin viewing the
// dashboard as a partner gets that partner's bookings; its identity changes
// with the ghost target, and it is in the effect deps, so entering and leaving
// ghost mode refetches on its own.

export interface BookingsFeed {
  resource: Resource<Booking[]>
  /** Null once the last page has been read. */
  nextCursor: string | null
  loadMore: () => void
  isLoadingMore: boolean
  /** Set when a load-more failed; the rows already shown stay put. */
  loadMoreFailed: boolean
}

export const useBookings = (params: {
  status: BookingStatusFilter
}): BookingsFeed => {
  const apiFetch = useApiFetch()
  const { status } = params

  const [resource, setResource] = useState<Resource<Booking[]>>({
    status: 'loading',
  })
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadMoreFailed, setLoadMoreFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setResource({ status: 'loading' })
    setNextCursor(null)
    setLoadMoreFailed(false)

    void (async () => {
      try {
        const result = await fetchBookings(apiFetch, { status }, controller.signal)
        if (controller.signal.aborted) return
        if (!result.ok) {
          setResource({ status: 'failed', failure: result.failure })
          return
        }
        setResource({ status: 'ready', data: result.data.bookings })
        setNextCursor(result.data.nextCursor)
      } catch {
        // Aborted.
      }
    })()

    return () => controller.abort()
  }, [apiFetch, status])

  const loadMore = useCallback(() => {
    if (nextCursor === null || isLoadingMore) return

    setIsLoadingMore(true)
    setLoadMoreFailed(false)

    void (async () => {
      const controller = new AbortController()
      try {
        const result = await fetchBookings(
          apiFetch,
          { status, cursor: nextCursor },
          controller.signal
        )
        if (!result.ok) {
          setLoadMoreFailed(true)
          return
        }
        // Append rather than replace, and keep the existing rows if the state
        // moved on — a failed page must not discard what the reader already has.
        setResource(current =>
          current.status === 'ready'
            ? { status: 'ready', data: [...current.data, ...result.data.bookings] }
            : current
        )
        setNextCursor(result.data.nextCursor)
      } catch {
        setLoadMoreFailed(true)
      } finally {
        setIsLoadingMore(false)
      }
    })()
  }, [apiFetch, status, nextCursor, isLoadingMore])

  return { resource, nextCursor, loadMore, isLoadingMore, loadMoreFailed }
}
